import { useEffect, useState } from "react";
import api from "../../lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../components/ui/select";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { fmt } from "../../lib/money";
import { MapPin, Phone, Mail, User as UserIcon, Copy, ChevronDown, ChevronUp, Search, X, Tag, MessageCircle, Trash2 } from "lucide-react";

const STATUS_COLORS = {
  placed: "bg-[#0033FF] text-white",
  shipped: "bg-black text-white",
  delivered: "bg-[#00A355] text-white",
  cancelled: "bg-[#FF2A00] text-white",
};

function copyText(text) {
  try {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  } catch {
    toast.error("Copy failed");
  }
}

function formatAddress(a) {
  return [a.full_name, a.line1, a.line2, `${a.city}, ${a.state} ${a.postal_code}`, a.country, a.phone]
    .filter(Boolean)
    .join("\n");
}

export default function SellerOrders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatusFilter] = useState("all");

  const load = () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (status !== "all") params.set("status", status);
    const qs = params.toString();
    api.get(`/seller/orders${qs ? `?${qs}` : ""}`).then((r) => {
      setOrders(r.data);
      if (r.data.length === 1) setOpenId(r.data[0].order_id);
    }).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  // Clear the header bell badge — sellers are looking at their orders now.
  useEffect(() => {
    api.post("/notifications/mark-seen").catch(() => {});
    try { window.dispatchEvent(new Event("orders-marked-seen")); } catch { /* ignore */ }
  }, []);

  const onSearch = (e) => { e.preventDefault(); load(); };
  const clearSearch = () => { setQ(""); setStatusFilter("all"); setTimeout(load, 0); };

  const setStatus = async (id, newStatus) => {
    try {
      await api.put(`/seller/orders/${id}/status`, { status: newStatus });
      toast.success("Status updated");
      load();
      // Auto-open WhatsApp message dialog for status changes that warrant
      // customer notification (placed→shipped, shipped→delivered, *→cancelled,
      // and even the initial placed→placed update is a no-op — only fire on
      // shipped/delivered/cancelled).
      if (newStatus === "shipped" || newStatus === "delivered" || newStatus === "cancelled") {
        try {
          const { data } = await api.get(`/seller/orders/${id}/whatsapp?status=${newStatus}`);
          // Prompt the seller — they tap "Send" to actually open WhatsApp
          openWhatsApp(data, newStatus);
        } catch (err) {
          // Customer might not have a phone number; just notify
          if (err?.response?.status === 400) {
            toast(err.response.data?.detail || "Customer has no phone on file — WhatsApp skipped");
          }
        }
      }
    } catch {
      toast.error("Failed");
    }
  };

  const openWhatsApp = (payload, statusLabel) => {
    // Show a confirm dialog so the seller can review the message before
    // sending. Native confirm() is enough — opens the deeplink on OK.
    const ok = window.confirm(
      `Notify customer on WhatsApp about "${statusLabel}"?\n\n${payload.message}\n\nTap OK to open WhatsApp.`
    );
    if (ok) {
      window.open(payload.wa_url, "_blank", "noopener,noreferrer");
    }
  };

  const sendWhatsAppFor = async (orderId, st) => {
    try {
      const { data } = await api.get(`/seller/orders/${orderId}/whatsapp?status=${st}`);
      window.open(data.wa_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't build WhatsApp message");
    }
  };

  const deleteOrder = async (orderId) => {
    if (user?.role !== "admin") {
      toast.error("Only admins can delete orders");
      return;
    }
    const restore = window.confirm(
      `Delete order ${orderId}?\n\nTap OK to ALSO restore the ordered units back to product stock (recommended for cancellations).\nTap Cancel to delete WITHOUT restoring stock.`
    );
    // Either choice proceeds with delete; we just track whether to restore stock
    const reallyDelete = window.confirm(
      "This permanently removes the order. A tombstone is kept in `deleted_orders` for audit.\n\nProceed?"
    );
    if (!reallyDelete) return;
    try {
      await api.delete(`/admin/orders/${orderId}?restore_stock=${restore}`);
      toast.success("Order deleted" + (restore ? " · stock restored" : ""));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const statusCount = (s) => orders.filter((o) => o.status === s).length;

  return (
    <div>
      <h1 className="font-display font-black text-4xl tracking-tight">Orders</h1>
      <p className="mt-2 text-sm text-neutral-600">Tap an order to see the delivery address and buyer contact info.</p>

      {/* Search + status filters */}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <form onSubmit={onSearch} className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input
            data-testid="seller-orders-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search order id, customer, or product…"
            className="w-full h-10 pl-10 pr-9 border border-black rounded-none text-sm"
          />
          {q && (
            <button
              type="button"
              onClick={() => { setQ(""); setTimeout(load, 0); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-black"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </form>
        <div className="flex flex-wrap gap-1">
          {[
            { k: "all", label: "All" },
            { k: "placed", label: "Placed" },
            { k: "shipped", label: "Shipped" },
            { k: "delivered", label: "Delivered" },
            { k: "cancelled", label: "Cancelled" },
          ].map((s) => (
            <button
              key={s.k}
              data-testid={`seller-orders-status-${s.k}`}
              type="button"
              onClick={() => setStatusFilter(s.k)}
              className={`h-10 px-3 text-xs uppercase tracking-widest font-bold border border-black transition-colors ${
                status === s.k ? "bg-black text-white" : "bg-white text-black hover:bg-neutral-100"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        {(q || status !== "all") && (
          <button
            type="button"
            onClick={clearSearch}
            className="text-xs text-neutral-500 hover:text-black underline"
          >
            Clear
          </button>
        )}
      </div>

      <div className="mt-6 border border-neutral-200">
        {orders.length === 0 && <div className="p-10 text-center text-neutral-500" data-testid="seller-orders-empty">{q || status !== "all" ? "No orders match your filters." : "No orders yet."}</div>}
        {orders.map((o) => {
          const myItems = o.items.filter((i) => i.seller_id === user.user_id);
          const myTotal = myItems.reduce((s, it) => s + it.price * it.quantity, 0);
          // Discount the buyer used that came out of THIS seller's revenue
          // (only item-level types — percent / fixed; shipping waivers don't apply).
          const myDiscount = (o.discount_breakdown || [])
            .filter((d) => d.seller_id === user.user_id && ["percent", "fixed", "fixed_plus_free_shipping", "percent_plus_free_shipping"].includes(d.discount_type))
            .reduce((s, d) => s + (d.amount || 0), 0);
          const myNet = Math.max(0, myTotal - myDiscount);
          const isOpen = openId === o.order_id;
          const a = o.shipping_address;
          return (
            <div
              key={o.order_id}
              data-testid={`seller-order-${o.order_id}`}
              className="border-b border-neutral-200 last:border-b-0"
            >
              {/* Row header */}
              <div className="flex items-center justify-between p-4 gap-4 flex-wrap">
                <button
                  onClick={() => setOpenId(isOpen ? null : o.order_id)}
                  data-testid={`toggle-${o.order_id}`}
                  className="flex items-center gap-3 text-left hover:text-[#0033FF] transition-colors"
                >
                  {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  <div>
                    <div className="font-display font-bold underline underline-offset-4 decoration-dotted">
                      #{o.order_id.slice(-8)}
                    </div>
                    <div className="text-xs text-neutral-500">
                      {new Date(o.created_at).toLocaleString()} · <b className="text-black">{a.full_name}</b> · {myItems.length} item{myItems.length > 1 ? "s" : ""}
                    </div>
                    {o.discount_codes && o.discount_codes.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1" data-testid={`discount-codes-${o.order_id}`}>
                        {o.discount_codes.map((c) => {
                          const entry = (o.discount_breakdown || []).find((e) => e.code === c);
                          return (
                            <span
                              key={c}
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-[#FFE600] text-black border border-black"
                              title={entry ? `Saved ${entry.amount} via ${c}` : c}
                            >
                              <Tag className="w-3 h-3" /> {c}
                              {entry && <span className="font-normal normal-case tracking-normal text-neutral-700">−{fmt(entry.amount || 0)}</span>}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {!isOpen && (
                      <div className="text-[11px] uppercase tracking-widest text-[#0033FF] font-bold mt-1">
                        Click to see delivery address, phone &amp; email →
                      </div>
                    )}
                  </div>
                </button>
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 ${STATUS_COLORS[o.status]}`}>{o.status}</span>
                  <Select value={o.status} onValueChange={(v) => setStatus(o.order_id, v)}>
                    <SelectTrigger data-testid={`status-select-${o.order_id}`} className="rounded-none border-black h-9 w-36"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none border-black">
                      <SelectItem value="placed">placed</SelectItem>
                      <SelectItem value="shipped">shipped</SelectItem>
                      <SelectItem value="delivered">delivered</SelectItem>
                      <SelectItem value="cancelled">cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  {user?.role === "admin" && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); deleteOrder(o.order_id); }}
                      data-testid={`delete-order-${o.order_id}`}
                      title="Delete order (admin only)"
                      className="h-9 w-9 inline-flex items-center justify-center border border-black hover:bg-[#FF2A00] hover:text-white hover:border-[#FF2A00]"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Expanded details */}
              {isOpen && (
                <div className="px-4 pb-5 grid lg:grid-cols-[1.1fr_1fr] gap-5">
                  {/* Delivery / contact card */}
                  <div className="border border-black p-4 bg-neutral-50" data-testid={`delivery-${o.order_id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-display font-bold uppercase tracking-wider text-sm flex items-center gap-2">
                        <MapPin className="w-4 h-4" /> Ship to
                      </h3>
                      <button
                        data-testid={`copy-address-${o.order_id}`}
                        onClick={() => copyText(formatAddress(a))}
                        className="text-xs font-semibold flex items-center gap-1 hover:text-[#0033FF]"
                      >
                        <Copy className="w-3.5 h-3.5" /> Copy address
                      </button>
                    </div>
                    <div className="text-sm space-y-0.5">
                      <div className="font-display font-bold text-base">{a.full_name}</div>
                      <div>{a.line1}</div>
                      {a.line2 && <div>{a.line2}</div>}
                      <div>{a.city}, {a.state} {a.postal_code}</div>
                      <div>{a.country}</div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-neutral-300 space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-neutral-600" />
                        <a href={`tel:${a.phone}`} className="font-semibold hover:text-[#0033FF]" data-testid={`phone-${o.order_id}`}>
                          {a.phone}
                        </a>
                        <button onClick={() => copyText(a.phone)} className="ml-auto text-neutral-500 hover:text-black">
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {o.customer_email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-neutral-600" />
                          <a href={`mailto:${o.customer_email}`} className="font-semibold hover:text-[#0033FF] truncate" data-testid={`email-${o.order_id}`}>
                            {o.customer_email}
                          </a>
                          <button onClick={() => copyText(o.customer_email)} className="ml-auto text-neutral-500 hover:text-black">
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                      {o.customer_name && o.customer_name !== a.full_name && (
                        <div className="flex items-center gap-2 text-neutral-600">
                          <UserIcon className="w-4 h-4" />
                          <span>Account: {o.customer_name}</span>
                        </div>
                      )}
                      {a.phone && (
                        <div className="mt-2 grid grid-cols-2 gap-1.5">
                          <a
                            href={`https://wa.me/${a.phone.replace(/\D+/g, "")}?text=${encodeURIComponent(`Hi ${a.full_name}, this is about your order #${o.order_id.slice(-8)}.`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`whatsapp-buyer-${o.order_id}`}
                            className="col-span-2 inline-flex items-center justify-center gap-2 h-9 px-3 bg-[#25D366] hover:bg-[#1ebe5d] text-white text-sm font-semibold"
                          >
                            <MessageCircle className="w-4 h-4" /> Message buyer on WhatsApp
                          </a>
                          <button
                            type="button"
                            onClick={() => sendWhatsAppFor(o.order_id, "shipped")}
                            data-testid={`whatsapp-shipped-${o.order_id}`}
                            className="inline-flex items-center justify-center gap-1 h-8 px-2 border border-[#25D366] text-[#1ebe5d] hover:bg-[#25D366] hover:text-white text-[11px] font-semibold uppercase tracking-wider"
                          >
                            Notify: shipped
                          </button>
                          <button
                            type="button"
                            onClick={() => sendWhatsAppFor(o.order_id, "delivered")}
                            data-testid={`whatsapp-delivered-${o.order_id}`}
                            className="inline-flex items-center justify-center gap-1 h-8 px-2 border border-[#25D366] text-[#1ebe5d] hover:bg-[#25D366] hover:text-white text-[11px] font-semibold uppercase tracking-wider"
                          >
                            Notify: delivered
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Items */}
                  <div className="border border-neutral-200 p-4">
                    <h3 className="font-display font-bold uppercase tracking-wider text-sm mb-3">Items to fulfil</h3>
                    <div className="space-y-3">
                      {myItems.map((it, idx) => (
                        <div key={`${it.product_id}-${idx}`} className="flex justify-between text-sm border-b border-neutral-200 pb-2 last:border-b-0">
                          <div>
                            <div className="font-display font-bold">{it.title}</div>
                            <div className="text-xs text-neutral-500 mt-0.5">
                              Qty {it.quantity} · {fmt(it.price)}
                              {it.size && <span> · Size: <b>{it.size}</b></span>}
                              {it.color && <span> · Color: <b>{it.color}</b></span>}
                            </div>
                          </div>
                          <span className="font-display font-black">{fmt(it.price * it.quantity)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-3 border-t border-neutral-300 space-y-1 text-sm" data-testid={`seller-totals-${o.order_id}`}>
                      <div className="flex justify-between text-neutral-600">
                        <span>Sold for</span>
                        <span className="font-mono">{fmt(myTotal)}</span>
                      </div>
                      {myDiscount > 0 && (
                        <div className="flex justify-between text-[#FF9500]" data-testid={`seller-discount-${o.order_id}`}>
                          <span>
                            Discount{" "}
                            {(o.discount_breakdown || [])
                              .filter((d) => d.seller_id === user.user_id && ["percent","fixed","fixed_plus_free_shipping","percent_plus_free_shipping"].includes(d.discount_type))
                              .map((d) => (
                                <span key={d.code} className="font-bold">
                                  ({d.code}{myTotal > 0 && d.discount_type === "percent" ? ` · ${Math.round((d.amount / myTotal) * 100)}%` : ""})
                                </span>
                              ))}
                          </span>
                          <span className="font-mono font-bold">−{fmt(myDiscount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-display font-black text-base pt-1 border-t border-neutral-200">
                        <span>You collect</span>
                        <span className="text-[#00A355]" data-testid={`seller-collect-${o.order_id}`}>{fmt(myNet)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
